/*
 * Purpose: Entry point for Access feature; mounts AccessShell with login/register forms.
 * Boundary: Top-level page component; composes shell and services; no local logic.
 * Invariants:
 * - Provides transport and auth services to AccessShell and AuthForm.
 * - Renders login by default; register available as alternate tab.
 * - ASCII only; all comments and strings are plain ASCII.
 */

// Imports
import React from "react";
import { createRoot } from "react-dom/client";
import AccessShell from "../../core/components/access/accessshell";
import AuthForm from "../../core/components/access/authform";
import transport from "../../core/services/transport.js";
import auth from "../../core/services/auth.access.service.js";
import { ToastProvider } from "../../core/components/toast/toast-slim";

// Services - combined dependencies for forms and shell
const services = { transport, auth };

// Component AccessPageJSX:
// Page-level component rendering AccessShell with login/register tabs
// Receives services as props for injection
function AccessPageJSX({ services }) {
    // Derived - read referral code from URL query param (once, on mount)
    const referralCode = new URLSearchParams(window.location.search).get('ref');

    // Rendering - compose AccessShell with login and register render props
    return (
        <ToastProvider position="bottom-right">
            <AccessShell
                services={services}
                defaultMode="login"
                referralCode={referralCode}
                renderLogin={() => <AuthForm mode="login" services={services} />}
                renderRegister={() => <AuthForm mode="register" services={services} />}
            />
        </ToastProvider>
    );
}

// Export - default for route-level usage
export default AccessPageJSX;

// Bootstrap - mount app on #root when element exists
const mount = document.getElementById("root");
if (mount) {
    createRoot(mount).render(
        <React.StrictMode>
            <AccessPageJSX services={services} />
        </React.StrictMode>
    );
}